import React, { useState, useEffect } from 'react';
import { Plus, Eye, X, AlertCircle, CheckCircle, User, Building, DollarSign, ChevronDown, ChevronUp } from 'lucide-react';
import { getApiUrl, getAuthHeaders } from '../utils/auth';
import Pagination, { usePagination } from './Pagination';
import StyledDropdown from './StyledDropdown';

const InvestorEnrollment = ({ user }) => {
  const isAdmin = user?.role === 'Superadmin' || user?.role === 'Admin';

  const [enrollments, setEnrollments] = useState([]);
  const [investors, setInvestors] = useState([]);
  const [products, setProducts] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingEnrollment, setViewingEnrollment] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingEnrollment, setEditingEnrollment] = useState(null);
  const [expandedDividends, setExpandedDividends] = useState({});
  const [filterStatus, setFilterStatus] = useState('');
  const [filterInvestorId, setFilterInvestorId] = useState('');

  const [formData, setFormData] = useState({
    investorId: '',
    productDetailsId: '',
    agentId: '',
    bankName: '',
    bankAccountNumber: '',
    amount: '',
    startDate: new Date().toISOString().split('T')[0],
    endDate: '',
    paymentReceivedDate: '',
    status: 'Active',
    jointName: '',
    groupId: '',
  });

  const [editData, setEditData] = useState({
    amount: '',
    status: 'Active',
    paymentReceivedDate: '',
    jointName: '',
  });
  const [showJointName, setShowJointName] = useState(false);

  const fetchEnrollments = async () => {
    try {
      const params = new URLSearchParams();
      if (filterStatus) params.set('status', filterStatus);
      if (filterInvestorId) params.set('investorId', filterInvestorId);
      const url = `${getApiUrl()}/api/investor-enrollments${params.toString() ? `?${params}` : ''}`;
      const res = await fetch(url, { headers: getAuthHeaders() });
      const result = await res.json();
      if (result.success) setEnrollments(result.data);
    } catch (err) {
      console.error('Fetch enrollments error:', err);
      setError('Unable to fetch enrollments');
    }
  };

  const fetchInvestors = async () => {
    try {
      const url = isAdmin ? `${getApiUrl()}/api/investors/all` : `${getApiUrl()}/api/investors/my-investors`;
      const res = await fetch(url, { headers: getAuthHeaders() });
      const result = await res.json();
      if (result.success) {
        const approved = (result.data || []).filter((i) => i.status === 'Approved' || i.status === 'Active');
        setInvestors(approved);
      }
    } catch (err) {
      console.error('Fetch investors error:', err);
    }
  };

  const fetchProducts = async () => {
    try {
      const res = await fetch(`${getApiUrl()}/api/investors/products`, { headers: getAuthHeaders() });
      const result = await res.json();
      if (result.success) setProducts(result.data || []);
    } catch (err) {
      console.error('Fetch products error:', err);
    }
  };

  const fetchAgents = async () => {
    if (!isAdmin) return;
    try {
      const res = await fetch(`${getApiUrl()}/api/users/agents`, { headers: getAuthHeaders() });
      const result = await res.json();
      if (result.success) setAgents(result.data || []);
    } catch (err) {
      console.error('Fetch agents error:', err);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      await Promise.all([fetchEnrollments(), fetchInvestors(), fetchProducts(), fetchAgents()]);
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [filterStatus, filterInvestorId, isAdmin]);

  const resetForm = () => {
    setFormData({
      investorId: '',
      productDetailsId: '',
      agentId: '',
      bankName: '',
      bankAccountNumber: '',
      amount: '',
      startDate: new Date().toISOString().split('T')[0],
      endDate: '',
      paymentReceivedDate: '',
      status: 'Active',
      jointName: '',
      groupId: '',
    });
    setShowJointName(false);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      setError('');
      setSuccess('');
      const payload = {
        investorId: formData.investorId,
        productDetailsId: parseInt(formData.productDetailsId),
        agentId: formData.agentId,
        bankName: formData.bankName,
        bankAccountNumber: formData.bankAccountNumber,
        amount: parseFloat(String(formData.amount).replace(/,/g, '')),
        startDate: formData.startDate,
        status: formData.status,
      };
      if (formData.endDate) payload.endDate = formData.endDate;
      if (formData.paymentReceivedDate) payload.paymentReceivedDate = formData.paymentReceivedDate;
      if (formData.jointName?.trim()) payload.jointName = formData.jointName.trim();
      if (formData.groupId) payload.groupId = parseInt(formData.groupId, 10);

      const response = await fetch(`${getApiUrl()}/api/investor-enrollments`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });
      const result = await response.json();
      if (result.success) {
        setSuccess(result.message);
        setShowCreateModal(false);
        resetForm();
        fetchEnrollments();
        setTimeout(() => setSuccess(''), 5000);
      } else {
        setError(result.message || 'Failed to create enrollment');
      }
    } catch (err) {
      console.error('Create enrollment error:', err);
      setError('Failed to create enrollment');
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!editingEnrollment) return;
    try {
      setError('');
      setSuccess('');
      const payload = {};
      if (editData.amount !== undefined && editData.amount !== '') {
        const parsed = parseFloat(String(editData.amount).replace(/,/g, ''));
        if (!isNaN(parsed) && parsed > 0) payload.amount = parsed;
      }
      if (editData.status !== undefined) payload.status = editData.status;
      if (editData.paymentReceivedDate !== undefined) payload.paymentReceivedDate = editData.paymentReceivedDate || null;
      if (editData.jointName !== undefined) payload.jointName = editData.jointName.trim() || null;

      const response = await fetch(`${getApiUrl()}/api/investor-enrollments/${editingEnrollment.id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });
      const result = await response.json();
      if (result.success) {
        setSuccess(result.message);
        setShowEditModal(false);
        setEditingEnrollment(null);
        fetchEnrollments();
        if (viewingEnrollment?.id === editingEnrollment.id) setViewingEnrollment(result.data);
        setTimeout(() => setSuccess(''), 5000);
      } else {
        setError(result.message || 'Failed to update enrollment');
      }
    } catch (err) {
      console.error('Update enrollment error:', err);
      setError('Failed to update enrollment');
    }
  };

  const openViewModal = (enrollment) => {
    setViewingEnrollment(enrollment);
    setShowViewModal(true);
  };

  const openEditModal = (enrollment) => {
    setEditingEnrollment(enrollment);
    const amount = enrollment.amount != null ? String(enrollment.amount) : '';
    setEditData({
      amount,
      status: enrollment.status,
      paymentReceivedDate: enrollment.paymentReceivedDate ? enrollment.paymentReceivedDate.split('T')[0] : '',
      jointName: enrollment.jointName || '',
    });
    setShowEditModal(true);
  };

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-MY', { style: 'currency', currency: 'MYR' }).format(amount);

  const formatNumber = (value) => {
    if (value == null || value === '') return '';
    const num = parseFloat(String(value).replace(/,/g, ''));
    if (isNaN(num)) return '';
    return new Intl.NumberFormat('en-MY').format(num);
  };

  const formatDate = (dateString) =>
    dateString
      ? new Date(dateString).toLocaleDateString('en-MY', { year: 'numeric', month: 'short', day: 'numeric' })
      : '—';

  const getStatusBadge = (status) => {
    const styles = {
      Active: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300',
      Completed: 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300',
      Cancelled: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300',
    };
    return (
      <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[status] || styles.Active}`}>
        {status}
      </span>
    );
  };

  const getDividendStatusBadge = (status) => {
    const styles = { Pending: 'bg-yellow-100 text-yellow-800', Paid: 'bg-green-100 text-green-800', Overdue: 'bg-red-100 text-red-800' };
    return <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${styles[status] || styles.Pending}`}>{status}</span>;
  };

  const toggleDividendExpand = (enrollmentId) => {
    setExpandedDividends((prev) => ({ ...prev, [enrollmentId]: !prev[enrollmentId] }));
  };

  const selectedProductDetails = products.find((p) => p.id === parseInt(formData.productDetailsId));
  const computedEndDate = formData.startDate && selectedProductDetails?.tenureMonths
    ? (() => {
        const d = new Date(formData.startDate);
        d.setMonth(d.getMonth() + selectedProductDetails.tenureMonths);
        return d.toISOString().split('T')[0];
      })()
    : '';

  const { currentPage, paginatedItems, totalItems, handlePageChange, resetPage } = usePagination(enrollments, 10);
  useEffect(() => {
    resetPage();
  }, [filterStatus, filterInvestorId]);

  if (loading && enrollments.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-dark-text">Investor Enrollment</h1>
          <p className="text-gray-600 dark:text-dark-muted mt-1">
            Create and manage enrollments (investor + product + bank + amount). Link to a sale (agent_id optional), bank, amount, start/end date, status.
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            resetForm();
            setShowCreateModal(true);
          }}
          className="flex items-center gap-2 bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors"
        >
          <Plus size={20} />
          New Enrollment
        </button>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-2 text-red-700 dark:text-red-300">
          <AlertCircle size={20} />
          {error}
        </div>
      )}
      {success && (
        <div className="mb-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg flex items-center gap-2 text-green-700 dark:text-green-300">
          <CheckCircle size={20} />
          {success}
        </div>
      )}

      <div className="bg-white dark:bg-dark-card rounded-lg shadow-sm border border-gray-200 dark:border-dark-border p-4 mb-6 flex flex-wrap gap-4">
        <StyledDropdown
          options={[
            { value: '', label: 'All Status' },
            { value: 'Active', label: 'Active' },
            { value: 'Completed', label: 'Completed' },
            { value: 'Cancelled', label: 'Cancelled' },
          ]}
          value={filterStatus}
          onChange={setFilterStatus}
          placeholder="All Status"
          showClear
          className="min-w-[150px]"
        />
        <StyledDropdown
          options={[
            { value: '', label: 'All Investors' },
            ...investors.map((i) => ({ value: i.id, label: `${i.name} (${i.email})` })),
          ]}
          value={filterInvestorId}
          onChange={setFilterInvestorId}
          placeholder="All Investors"
          showClear
          className="min-w-[200px]"
        />
      </div>

      <div className="bg-white dark:bg-dark-card rounded-lg shadow-sm border border-gray-200 dark:border-dark-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-slate-800/50 border-b border-gray-200 dark:border-dark-border">
              <tr>
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 dark:text-dark-muted uppercase tracking-wider">Investor</th>
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 dark:text-dark-muted uppercase tracking-wider">Product</th>
                <th className="text-right px-6 py-3 text-xs font-medium text-gray-500 dark:text-dark-muted uppercase tracking-wider">Amount</th>
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 dark:text-dark-muted uppercase tracking-wider">Start</th>
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 dark:text-dark-muted uppercase tracking-wider">End</th>
                <th className="text-center px-6 py-3 text-xs font-medium text-gray-500 dark:text-dark-muted uppercase tracking-wider">Status</th>
                {isAdmin && (
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 dark:text-dark-muted uppercase tracking-wider">Agent</th>
                )}
                <th className="text-center px-6 py-3 text-xs font-medium text-gray-500 dark:text-dark-muted uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-dark-border">
              {paginatedItems.length === 0 ? (
                <tr>
                  <td colSpan={isAdmin ? 8 : 7} className="px-6 py-12 text-center text-gray-500 dark:text-dark-muted">
                    <DollarSign size={48} className="mx-auto mb-4 text-gray-300 dark:text-slate-600" />
                    <p className="text-lg font-medium">No enrollments found</p>
                    <p className="text-sm">Create an enrollment to link an investor to a product</p>
                  </td>
                </tr>
              ) : (
                paginatedItems.map((enr) => (
                  <tr key={enr.id} className="hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900 dark:text-dark-text">{enr.investor?.name}</div>
                      {enr.jointName && (
                        <div className="text-xs text-primary-600 dark:text-primary-400">Joint: {enr.jointName}</div>
                      )}
                      <div className="text-xs text-gray-500 dark:text-dark-muted">{enr.investor?.email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-dark-text">
                      {enr.productDetails?.product?.name ?? 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium text-gray-900 dark:text-dark-text">
                      {formatCurrency(enr.amount)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-dark-muted">{formatDate(enr.startDate)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-dark-muted">{formatDate(enr.endDate)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">{getStatusBadge(enr.status)}</td>
                    {isAdmin && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-dark-muted">
                        {enr.agent ? `${enr.agent.name} — ${enr.agent.role?.name || ''}` : '—'}
                      </td>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="flex items-center justify-center gap-2">
                        <button type="button" onClick={() => openViewModal(enr)} className="text-primary-600 hover:text-primary-800 p-1" title="View">
                          <Eye size={18} />
                        </button>
                        <button type="button" onClick={() => openEditModal(enr)} className="text-gray-600 hover:text-gray-800 dark:hover:text-dark-text p-1" title="Edit">
                          Edit
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <Pagination currentPage={currentPage} totalItems={totalItems} itemsPerPage={10} onPageChange={handlePageChange} />
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-dark-card rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b dark:border-dark-border">
              <h2 className="text-lg font-semibold dark:text-dark-text">New Enrollment</h2>
              <button type="button" onClick={() => setShowCreateModal(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-dark-text">
                <X size={24} />
              </button>
            </div>
            <form onSubmit={handleCreate} className="p-4 space-y-4">
              <div>
                <h3 className="font-medium text-gray-900 dark:text-dark-text mb-3 flex items-center gap-2">
                  <User size={18} />
                  Investor & Product
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {isAdmin && (
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">Agent *</label>
                      <StyledDropdown
                        options={[{ value: '', label: 'Select agent' }, ...agents.map((a) => ({ value: a.id, label: `${a.name} — ${a.roleName}` }))]}
                        value={formData.agentId}
                        onChange={(val) => {
                          const agent = agents.find((a) => a.id === val);
                          const groups = agent?.groups || [];
                          setFormData({
                            ...formData,
                            agentId: val,
                            groupId: groups.length === 1 ? String(groups[0].id) : (agent?.groupId ? String(agent.groupId) : ''),
                          });
                        }}
                        placeholder="Select agent"
                        className="w-full"
                      />
                    </div>
                  )}
                  {(() => {
                    const agent = agents.find((a) => a.id === formData.agentId);
                    const agentGroups = agent?.groups || [];
                    if (agentGroups.length <= 1) return null;
                    return (
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">Group for this enrollment *</label>
                        <StyledDropdown
                          options={[{ value: '', label: 'Select group' }, ...agentGroups.map((g) => ({ value: String(g.id), label: g.name }))]}
                          value={formData.groupId}
                          onChange={(val) => setFormData({ ...formData, groupId: val })}
                          placeholder="Select group"
                          className="w-full"
                        />
                      </div>
                    );
                  })()}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">Investor *</label>
                    <StyledDropdown
                      options={[{ value: '', label: 'Select investor' }, ...investors.map((i) => ({ value: i.id, label: `${i.name} — ${i.email}` }))]}
                      value={formData.investorId}
                      onChange={(val) => setFormData({ ...formData, investorId: val })}
                      placeholder="Select investor"
                      className="w-full"
                    />
                  </div>
                  <div className="md:col-span-2">
                    {!showJointName && !formData.jointName ? (
                      <button
                        type="button"
                        onClick={() => setShowJointName(true)}
                        className="text-sm text-primary-600 dark:text-primary-400 hover:underline"
                      >
                        + Add joint name
                      </button>
                    ) : (
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <label className="block text-sm font-medium text-gray-700 dark:text-dark-text">Joint name</label>
                          <button
                            type="button"
                            onClick={() => {
                              setShowJointName(false);
                              setFormData({ ...formData, jointName: '' });
                            }}
                            className="text-xs text-gray-500 hover:text-red-600"
                          >
                            Remove
                          </button>
                        </div>
                        <input
                          type="text"
                          value={formData.jointName}
                          onChange={(e) => setFormData({ ...formData, jointName: e.target.value })}
                          className="w-full border border-gray-300 dark:border-dark-border dark:bg-dark-bg dark:text-dark-text rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                          placeholder="Second person's name (record only, no login)"
                        />
                        <p className="text-xs text-gray-500 dark:text-dark-muted mt-1">
                          Stored for statements only. Does not create an investor account.
                        </p>
                      </div>
                    )}
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">Product *</label>
                    <StyledDropdown
                      options={[{ value: '', label: 'Select product' }, ...products.map((p) => ({ value: String(p.id), label: `${p.product?.name} — ${p.tenureMonths}m, ${parseFloat(p.interestRate).toFixed(2)}% p.a., ${p.paymentMode}` }))]}
                      value={formData.productDetailsId ? String(formData.productDetailsId) : ''}
                      onChange={(val) => setFormData({ ...formData, productDetailsId: val })}
                      placeholder="Select product"
                      className="w-full"
                    />
                  </div>
                </div>
              </div>
              <div>
                <h3 className="font-medium text-gray-900 dark:text-dark-text mb-3 flex items-center gap-2">
                  <Building size={18} />
                  Bank & Amount
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">Bank name *</label>
                    <input
                      type="text"
                      value={formData.bankName}
                      onChange={(e) => setFormData({ ...formData, bankName: e.target.value })}
                      className="w-full border border-gray-300 dark:border-dark-border dark:bg-dark-bg dark:text-dark-text rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">Account number *</label>
                    <input
                      type="text"
                      value={formData.bankAccountNumber}
                      onChange={(e) => setFormData({ ...formData, bankAccountNumber: e.target.value })}
                      className="w-full border border-gray-300 dark:border-dark-border dark:bg-dark-bg dark:text-dark-text rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">Amount (RM) *</label>
                    <input
                      type="text"
                      value={formatNumber(formData.amount)}
                      onChange={(e) => {
                        const raw = e.target.value.replace(/,/g, '');
                        if (raw === '' || /^\d*\.?\d*$/.test(raw)) setFormData({ ...formData, amount: raw });
                      }}
                      className="w-full border border-gray-300 dark:border-dark-border dark:bg-dark-bg dark:text-dark-text rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">Start date *</label>
                    <input
                      type="date"
                      value={formData.startDate}
                      onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                      className="w-full border border-gray-300 dark:border-dark-border dark:bg-dark-bg dark:text-dark-text rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">End date (optional)</label>
                    <input
                      type="date"
                      value={formData.endDate || computedEndDate}
                      onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                      className="w-full border border-gray-300 dark:border-dark-border dark:bg-dark-bg dark:text-dark-text rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">Date *</label>
                    <input
                      type="date"
                      value={formData.paymentReceivedDate}
                      onChange={(e) => setFormData({ ...formData, paymentReceivedDate: e.target.value })}
                      required
                      className="w-full border border-gray-300 dark:border-dark-border dark:bg-dark-bg dark:text-dark-text rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">Status</label>
                    <StyledDropdown
                      options={[
                        { value: 'Active', label: 'Active' },
                        { value: 'Completed', label: 'Completed' },
                        { value: 'Cancelled', label: 'Cancelled' },
                      ]}
                      value={formData.status}
                      onChange={(val) => setFormData({ ...formData, status: val })}
                      placeholder="Select status"
                      className="w-full"
                    />
                  </div>
                </div>
              </div>
              <div className="flex gap-3 pt-4 border-t dark:border-dark-border">
                <button type="button" onClick={() => setShowCreateModal(false)} className="flex-1 px-4 py-2 border border-gray-300 dark:border-dark-border rounded-lg text-gray-700 dark:text-dark-text hover:bg-gray-50 dark:hover:bg-slate-700">
                  Cancel
                </button>
                <button type="submit" className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700">
                  Create Enrollment
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Modal */}
      {showViewModal && viewingEnrollment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-dark-card rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b dark:border-dark-border">
              <h2 className="text-lg font-semibold dark:text-dark-text">Enrollment Details</h2>
              <div className="flex items-center gap-3">
                {getStatusBadge(viewingEnrollment.status)}
                <button type="button" onClick={() => setShowViewModal(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-dark-text">
                  <X size={24} />
                </button>
              </div>
            </div>
            <div className="p-4 space-y-4">
              <div className="bg-gray-50 dark:bg-slate-800/50 rounded-lg p-4">
                <h3 className="font-medium text-gray-900 dark:text-dark-text mb-3">Investor</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                  <div><div className="text-gray-500 dark:text-dark-muted">Name</div><div className="font-medium dark:text-dark-text">{viewingEnrollment.investor?.name}</div></div>
                  <div><div className="text-gray-500 dark:text-dark-muted">Email</div><div className="font-medium dark:text-dark-text">{viewingEnrollment.investor?.email}</div></div>
                  {viewingEnrollment.jointName && (
                    <div><div className="text-gray-500 dark:text-dark-muted">Joint name</div><div className="font-medium dark:text-dark-text">{viewingEnrollment.jointName}</div></div>
                  )}
                </div>
              </div>
              <div className="bg-gray-50 dark:bg-slate-800/50 rounded-lg p-4">
                <h3 className="font-medium text-gray-900 dark:text-dark-text mb-3">Product & Amount</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div><div className="text-gray-500 dark:text-dark-muted">Product</div><div className="font-medium dark:text-dark-text">{viewingEnrollment.productDetails?.product?.name}</div></div>
                  <div><div className="text-gray-500 dark:text-dark-muted">Amount</div><div className="font-medium text-lg dark:text-dark-text">{formatCurrency(viewingEnrollment.amount)}</div></div>
                  <div><div className="text-gray-500 dark:text-dark-muted">Start</div><div className="font-medium dark:text-dark-text">{formatDate(viewingEnrollment.startDate)}</div></div>
                  <div><div className="text-gray-500 dark:text-dark-muted">End</div><div className="font-medium dark:text-dark-text">{formatDate(viewingEnrollment.endDate)}</div></div>
                  <div><div className="text-gray-500 dark:text-dark-muted">Bank</div><div className="font-medium dark:text-dark-text">{viewingEnrollment.bankName}</div></div>
                  <div><div className="text-gray-500 dark:text-dark-muted">Account</div><div className="font-medium dark:text-dark-text">{viewingEnrollment.bankAccountNumber}</div></div>
                  {viewingEnrollment.agent && (
                    <div><div className="text-gray-500 dark:text-dark-muted">Agent</div><div className="font-medium dark:text-dark-text">{viewingEnrollment.agent.name} — {viewingEnrollment.agent.role?.name || ''}</div></div>
                  )}
                </div>
              </div>
              {viewingEnrollment.dividendPayments?.length > 0 && (
                <div className="border dark:border-dark-border rounded-lg overflow-hidden">
                  <button type="button" onClick={() => toggleDividendExpand(viewingEnrollment.id)} className="flex items-center justify-between w-full p-4 text-left bg-primary-50 dark:bg-primary-900/20">
                    <span className="font-medium text-primary-800 dark:text-primary-300">Dividend Schedule ({viewingEnrollment.dividendPayments.length})</span>
                    {expandedDividends[viewingEnrollment.id] ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                  </button>
                  {expandedDividends[viewingEnrollment.id] && (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50 dark:bg-slate-800/50">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Year</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">#</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Due Date</th>
                            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Amount</th>
                            <th className="px-3 py-2 text-center text-xs font-medium text-gray-500">Status</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-dark-border">
                          {viewingEnrollment.dividendPayments.map((d, i) => (
                            <tr key={i}>
                              <td className="px-3 py-2 dark:text-dark-text">Year {d.year}</td>
                              <td className="px-3 py-2 dark:text-dark-text">{d.dividendNumber}</td>
                              <td className="px-3 py-2 dark:text-dark-text">{formatDate(d.dueDate)}</td>
                              <td className="px-3 py-2 text-right dark:text-dark-text">{formatCurrency(d.amount)}</td>
                              <td className="px-3 py-2 text-center">{getDividendStatusBadge(d.status)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
              <div className="flex gap-3">
                <button type="button" onClick={() => { setShowViewModal(false); openEditModal(viewingEnrollment); }} className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700">
                  Edit
                </button>
                <button type="button" onClick={() => setShowViewModal(false)} className="flex-1 px-4 py-2 bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-dark-text rounded-lg hover:bg-gray-200 dark:hover:bg-slate-600">
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && editingEnrollment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-dark-card rounded-lg shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b dark:border-dark-border">
              <h2 className="text-lg font-semibold dark:text-dark-text">Edit Enrollment</h2>
              <button type="button" onClick={() => setShowEditModal(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-dark-text">
                <X size={24} />
              </button>
            </div>
            <form onSubmit={handleUpdate} className="p-4 space-y-4">
              <p className="text-sm text-gray-600 dark:text-dark-muted">
                {editingEnrollment.investor?.name}{editingEnrollment.jointName ? ` & ${editingEnrollment.jointName}` : ''} — {editingEnrollment.productDetails?.product?.name}
              </p>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">Joint name (optional)</label>
                <input
                  type="text"
                  value={editData.jointName}
                  onChange={(e) => setEditData({ ...editData, jointName: e.target.value })}
                  className="w-full border border-gray-300 dark:border-dark-border dark:bg-dark-bg dark:text-dark-text rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="Second person's name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">Amount (MYR)</label>
                <input
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={editData.amount}
                  onChange={(e) => setEditData({ ...editData, amount: e.target.value })}
                  className="w-full border border-gray-300 dark:border-dark-border dark:bg-dark-bg dark:text-dark-text rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="e.g. 100000"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">Status</label>
                <StyledDropdown
                  options={[
                    { value: 'Active', label: 'Active' },
                    { value: 'Completed', label: 'Completed' },
                    { value: 'Cancelled', label: 'Cancelled' },
                  ]}
                  value={editData.status}
                  onChange={(val) => setEditData({ ...editData, status: val })}
                  placeholder="Select status"
                  className="w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">Payment history date (optional)</label>
                <input
                  type="date"
                  value={editData.paymentReceivedDate}
                  onChange={(e) => setEditData({ ...editData, paymentReceivedDate: e.target.value })}
                  className="w-full border border-gray-300 dark:border-dark-border dark:bg-dark-bg dark:text-dark-text rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowEditModal(false)} className="flex-1 px-4 py-2 border border-gray-300 dark:border-dark-border rounded-lg text-gray-700 dark:text-dark-text hover:bg-gray-50 dark:hover:bg-slate-700">
                  Cancel
                </button>
                <button type="submit" className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700">
                  Update
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvestorEnrollment;
